import React from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@material-ui/core";
import { IRemyndWarrantyDetailSaveReq } from "../types";
import { yupTrimToNull } from "src/utils/stringUtils";
import { formatDateTimeForTextField } from "src/utils/dateUtils";
import { warrantyTypes } from "src/utils/enum";

interface Props {
  label: string;
  open: boolean;
  defaultValue?: IRemyndWarrantyDetailSaveReq;
  handleCloseModal: () => void;
  handleSubmitModal: (value: IRemyndWarrantyDetailSaveReq) => void;
}

const schema = yup.object().shape({
  labelTh: yup.string().transform(yupTrimToNull),
  labelEn: yup.string().transform(yupTrimToNull),
  type: yup.string().required("กรุณาระบุ ประเภทการรับประกัน"),
  years: yup
    .number()
    .default(0)
    .min(0, "ต้องมากกว่าหรือเท่ากับ 0")
    .integer("ต้องเป็นจำนวนเต็ม"),
  months: yup
    .number()
    .default(0)
    .min(0, "ต้องมากกว่าหรือเท่ากับ 0")
    .integer("ต้องเป็นจำนวนเต็ม"),
  days: yup
    .number()
    .default(0)
    .min(0, "ต้องมากกว่าหรือเท่ากับ 0")
    .integer("ต้องเป็นจำนวนเต็ม"),
  remarkTh: yup.string().transform(yupTrimToNull),
  remarkEn: yup.string().transform(yupTrimToNull),
  effectiveFrom: yup.string(),
  effectiveUntil: yup.string()
});

const WarrantyDetailModal: React.FC<Props> = props => {
  const { open, label, defaultValue, handleCloseModal, handleSubmitModal } =
    props;

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors }
  } = useForm<IRemyndWarrantyDetailSaveReq>({
    defaultValues: {
      ...defaultValue,
      type: defaultValue?.type ?? "DURATION",
      effectiveFrom: defaultValue?.effectiveFrom
        ? formatDateTimeForTextField(defaultValue?.effectiveFrom)
        : undefined,
      effectiveUntil: defaultValue?.effectiveUntil
        ? formatDateTimeForTextField(defaultValue?.effectiveUntil)
        : undefined
    },
    resolver: yupResolver(schema)
  });

  const handleTransformAndSubmit = (form: IRemyndWarrantyDetailSaveReq) => {
    const isDuration = form.type === "DURATION";
    handleSubmitModal({
      ...defaultValue,
      ord: 0, // will be set at final submit
      type: form.type,
      labelTh: form.labelTh,
      labelEn: form.labelEn,
      remarkTh: form.remarkTh,
      remarkEn: form.remarkEn,
      years: isDuration ? form.years : undefined,
      months: isDuration ? form.months : undefined,
      days: isDuration ? form.days : undefined,
      effectiveFrom: form.effectiveFrom,
      effectiveUntil: form.effectiveUntil
    });
  };

  const type = watch("type");

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      scroll="body"
      aria-labelledby="form-dialog-title"
    >
      <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
        <DialogTitle id="form-dialog-title" disableTypography>
          <Typography variant="h3">
            {!defaultValue ? "เพิ่ม " : "แก้ไข "}
            {label}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name="labelTh"
                control={control}
                defaultValue={defaultValue?.labelEn ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="ชื่อ (TH)"
                    error={Boolean(errors.labelEn)}
                    helperText={errors.labelEn?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="labelEn"
                control={control}
                defaultValue={defaultValue?.labelEn ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="ชื่อ (EN)"
                    error={Boolean(errors.labelEn)}
                    helperText={errors.labelEn?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider light />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="remarkTh"
                control={control}
                defaultValue={defaultValue?.remarkTh ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="หมายเหตุ (TH)"
                    error={Boolean(errors.remarkTh)}
                    helperText={errors.remarkTh?.message}
                    multiline
                    rows={2}
                    rowsMax={16}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="remarkEn"
                control={control}
                defaultValue={defaultValue?.remarkEn ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="หมายเหตุ (EN)"
                    error={Boolean(errors.remarkEn)}
                    helperText={errors.remarkEn?.message}
                    multiline
                    rows={2}
                    rowsMax={16}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider light />
            </Grid>

            <Grid item container spacing={2} xs={12}>
              <Grid item xs={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">รูปแบบการรับประกัน</FormLabel>
                  <Box ml={1} mt={1}>
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup aria-label="type" {...field}>
                          {warrantyTypes.map((wt, idx) => (
                            <FormControlLabel
                              key={`warranty_type_option_${idx}`}
                              value={wt.value}
                              control={<Radio color="primary" />}
                              label={wt.label}
                            />
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </Box>
                </FormControl>
              </Grid>

              {type === "DURATION" && (
                <>
                  <Grid item xs={2}>
                    <Controller
                      name="years"
                      control={control}
                      defaultValue={defaultValue?.years ?? 0}
                      render={({ field }) => (
                        <TextField
                          type="number"
                          variant="outlined"
                          fullWidth
                          inputProps={{
                            min: 0,
                            step: 1
                          }}
                          {...field}
                          label="ปี"
                          error={Boolean(errors.years)}
                          helperText={errors.years?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Controller
                      name="months"
                      control={control}
                      defaultValue={defaultValue?.months ?? 0}
                      render={({ field }) => (
                        <TextField
                          type="number"
                          variant="outlined"
                          fullWidth
                          inputProps={{
                            min: 0,
                            step: 1
                          }}
                          {...field}
                          label="เดือน"
                          error={Boolean(errors.months)}
                          helperText={errors.months?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Controller
                      name="days"
                      control={control}
                      defaultValue={defaultValue?.days ?? 0}
                      render={({ field }) => (
                        <TextField
                          type="number"
                          variant="outlined"
                          fullWidth
                          inputProps={{
                            min: 0,
                            step: 1
                          }}
                          {...field}
                          label="วัน"
                          error={Boolean(errors.days)}
                          helperText={errors.days?.message}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider light />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="effectiveFrom"
                control={control}
                defaultValue={defaultValue?.effectiveFrom ?? ""}
                render={({ field }) => (
                  <TextField
                    type="datetime-local"
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="Effective From"
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(errors.effectiveFrom)}
                    helperText={errors.effectiveFrom?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="effectiveUntil"
                control={control}
                defaultValue={defaultValue?.effectiveUntil ?? ""}
                render={({ field }) => (
                  <TextField
                    type="datetime-local"
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="Effective Until"
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(errors.effectiveUntil)}
                    helperText={errors.effectiveUntil?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          {!defaultValue ? (
            <Button
              key="addBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Add
            </Button>
          ) : (
            <Button
              key="updateBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Update
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default WarrantyDetailModal;
